'use client';

import useSWR from 'swr';
import { visitCounselApi } from './visit-counsel.api';
import { orderApi } from '@/entities/order';
import type { DepartureLocationInfo } from '@/entities/order/model/order.types';
import type { VisitCounselState } from './visit-counsel.types';

export interface PlannerDayCounselByPlanner {
  visitCounselId: string;
  orderId: string;
  scheduleTime: string; // HH:mm
  location: DepartureLocationInfo | null;
  misoOrderId?: string;
  /** 평수 */
  supplyArea?: number;
  /** CounselState: APPOINTED(시작전), STARTED(진행중) */
  state?: VisitCounselState;
  /** 상담 시작 시각 (state fallback용) */
  startedAt?: string | null;
}

export interface UsePlannerDayCounselsByPlannerResult {
  counsels: PlannerDayCounselByPlanner[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}

async function fetchPlannerDayCounselsByPlanner(
  plannerId: string,
  date: string
): Promise<PlannerDayCounselByPlanner[]> {
  const result = await visitCounselApi.searchVisitCounsels({
    filterGroups: [
      {
        logic: 'AND' as const,
        filters: [
          {
            field: 'counselorId' as const,
            operator: 'EQUALS' as const,
            value: plannerId,
            values: null,
          },
          {
            field: 'scheduleDate' as const,
            operator: 'EQUALS' as const,
            value: date,
            values: null,
          },
        ],
      },
    ],
    sortBy: 'scheduleDate' as const,
    sortOrder: 'ASC' as const,
    pageSize: 50,
  });

  const sorted = [...result.items].sort((a, b) =>
    a.scheduleTime < b.scheduleTime ? -1 : a.scheduleTime > b.scheduleTime ? 1 : 0
  );

  const uniqueOrderIds = [...new Set(sorted.map((c) => c.orderId))];
  const orderMap = new Map<
    string,
    {
      misoOrderId: string;
      location: DepartureLocationInfo | null;
      supplyArea?: number;
    }
  >();

  if (uniqueOrderIds.length > 0) {
    const orders = await orderApi.getOrdersByIds(uniqueOrderIds);
    for (const order of orders) {
      orderMap.set(order.id, {
        misoOrderId: order.misoOrderId,
        location: order.assignedDepartureLocation ?? null,
        supplyArea: order.moving?.departure?.supplyArea,
      });
    }
  }

  return sorted.map((c) => {
    const orderInfo = orderMap.get(c.orderId);
    const supplyArea = c.supplyArea ?? orderInfo?.supplyArea;
    return {
      visitCounselId: c.id,
      orderId: c.orderId,
      scheduleTime: c.scheduleTime,
      location: orderInfo?.location ?? null,
      misoOrderId: orderInfo?.misoOrderId,
      supplyArea: supplyArea != null ? supplyArea : undefined,
      state: c.state,
      startedAt: c.startedAt,
    };
  });
}

/**
 * 플래너의 특정 날짜 상담 리스트 조회 (scheduleTime 순)
 * Draft 일정·구역 탭의 "오늘의 상담 리스트" 데이터용
 */
export function usePlannerDayCounselsByPlanner(
  plannerId: string,
  date: string,
  enabled = true
): UsePlannerDayCounselsByPlannerResult {
  const { data, error, isLoading, mutate } = useSWR<PlannerDayCounselByPlanner[]>(
    enabled && plannerId && date ? ['planner-day-counsels', plannerId, date] : null,
    () => fetchPlannerDayCounselsByPlanner(plannerId, date),
    { revalidateOnMount: true }
  );

  return {
    counsels: data ?? [],
    isLoading,
    error: error ? (error instanceof Error ? error : new Error('상담 조회 실패')) : null,
    refetch: () => mutate(),
  };
}
